import React, { useState } from "react";
import {
  Grid,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Container,
} from "@mui/material";
import video from "../../assets/newvideobanner.mp4";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const MissionVisionSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [showFullMission, setShowFullMission] = useState(false);
  const [showFullVision, setShowFullVision] = useState(false);

  const missionParagraphs = [
    "Our mission is to advance environmental research and education by fostering effective conservation management, pollution mitigation, and sustainable livelihood development.",
    "We are committed to building local capacity by empowering fishermen, students, and women's self-help groups.",
    "Through strategic partnerships with academic institutions, government agencies, NGOs, and volunteers, we provide essential field support and resources.",
    "By promoting inclusive, community-driven initiatives, we strive to protect ecosystems and ensure resilient, sustainable futures for local communities.",
  ];

  const visionParagraphs = [
    "At REEF, we envision a sustainable future where thriving marine ecosystems support and coexist with healthy, resilient human communities.",
    "We are committed to protecting ocean biodiversity and promoting responsible practices that ensure ecological integrity and long-term well-being for people and the planet.",
  ];

  // Shared button style
  const buttonStyle = {
    mt: 2,
    px: 2.5,
    py: 1,
    borderRadius: "50px",
    backgroundColor: "#000",
    color: "#fff",
    border: "1px solid #fff",
    textTransform: "uppercase",
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 600,
    letterSpacing: "0.2em",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1.5,
    mx: "auto",
    fontSize: "0.75rem",
    minWidth: "140px",
    "&:hover": {
      backgroundColor: "#111",
      borderColor: "#fff",
    },
  };

  return (
    <Box sx={{ maxWidth: "1400px", mx: "auto" }}>
      <Grid
        container
        sx={{
          height: { md: "95vh", xs: "auto" },
          width: "100%",
          overflow: "hidden",
        }}
      >
        {/* Left Video Side */}
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            height: { md: "100%", xs: "300px" },
          }}
        >
          <Box
            sx={{
              height: "100%",
              position: "relative",
              overflow: "hidden",
              backgroundColor: "#000",
            }}
          >
            <video
              src={video}
              autoPlay
              muted
              loop
              playsInline
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        </Grid>

        {/* Right Text Side */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            height: { md: "100%", xs: "auto" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Mission Box */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#F8D36E",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 4,
              py: 4,
              textAlign: "center",
              height: { md: "50%", xs: "auto" },
            }}
          >
            <Box maxWidth="500px" width="100%">
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: { md: "35px", xs: "22px" },
                  mb: 2,
                  fontFamily: "Montserrat, sans-serif",
                }}
              >
                OUR MISSION
              </Typography>
              <Box
                sx={{
                  maxHeight: showFullMission ? "200px" : "80px",
                  overflowY: "auto",
                  transition: "max-height 0.3s ease",
                  pr: 1,
                }}
              >
                {missionParagraphs.map((para, index) => (
                  <Typography
                    key={index}
                    variant="body1"
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      lineHeight: 1.7,
                      fontFamily: "Montserrat, sans-serif",
                      mb: index !== missionParagraphs.length - 1 ? 2 : 0,
                    }}
                  >
                    {para}
                  </Typography>
                ))}
              </Box>
              <Button
                variant="contained"
                onClick={() => setShowFullMission(!showFullMission)}
                sx={buttonStyle}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: 12, color: "#000" }} />
                </Box>
                {showFullMission ? "LESS DETAIL" : "MORE DETAIL"}
              </Button>
            </Box>
          </Box>

          {/* Vision Box */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#DDE3E9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 4,
              py: 4,
              textAlign: "center",
              height: { md: "50%", xs: "auto" },
            }}
          >
            <Box maxWidth="500px" width="100%">
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: { md: "35px", xs: "22px" },
                  mb: 2,
                  fontFamily: "Montserrat, sans-serif",
                }}
              >
                OUR VISION
              </Typography>
              <Box
                sx={{
                  maxHeight: showFullVision ? "200px" : "80px",
                  overflowY: "auto",
                  transition: "max-height 0.3s ease",
                  pr: 1,
                }}
              >
                {visionParagraphs.map((para, index) => (
                  <Typography
                    key={index}
                    variant="body1"
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      lineHeight: 1.7,
                      fontFamily: "Montserrat, sans-serif",
                      mb: index !== visionParagraphs.length - 1 ? 2 : 0,
                    }}
                  >
                    {para}
                  </Typography>
                ))}
              </Box>
              <Button
                onClick={() => setShowFullVision(!showFullVision)}
                sx={buttonStyle}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: 12, color: "#000" }} />
                </Box>
                {showFullVision ? "LESS DETAIL" : "MORE DETAIL"}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MissionVisionSection;
